import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Download, FileText, ChevronLeft, ChevronRight, Calendar, 
  Filter, X, Loader2, FileSpreadsheet, BarChart3, Search,
  ArrowUp, ArrowDown, ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';

// Utility to export data as CSV
const exportToCSV = (data, filename, columns) => {
  if (!data || data.length === 0) {
    toast.error('No data to export');
    return;
  }
  
  const headers = columns.map(c => c.label).join(',');
  const rows = data.map(row => 
    columns.map(c => {
      let val = row[c.key];
      if (val === null || val === undefined) val = '';
      if (typeof val === 'string' && val.includes(',')) val = `"${val}"`;
      return val;
    }).join(',')
  );
  
  const csv = [headers, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success('CSV exported successfully');
};

// Utility to export data as PDF (simple version using print)
const exportToPDF = (title, data, columns) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    toast.error('Please allow popups for PDF export');
    return;
  }
  
  const tableRows = data.map(row => 
    `<tr>${columns.map(c => `<td style="padding: 8px; border: 1px solid #ddd;">${row[c.key] ?? '-'}</td>`).join('')}</tr>`
  ).join('');
  
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h1 { color: #1f2937; margin-bottom: 20px; }
        table { border-collapse: collapse; width: 100%; }
        th { background: #f3f4f6; padding: 10px; border: 1px solid #ddd; text-align: left; }
        td { padding: 8px; border: 1px solid #ddd; }
        .meta { color: #6b7280; margin-bottom: 20px; }
      </style>
    </head>
    <body>
      <h1>${title}</h1>
      <p class="meta">Generated: ${new Date().toLocaleString()} | Records: ${data.length}</p>
      <table>
        <thead>
          <tr>${columns.map(c => `<th>${c.label}</th>`).join('')}</tr>
        </thead>
        <tbody>${tableRows}</tbody>
      </table>
      <script>window.onload = () => { window.print(); }</script>
    </body>
    </html>
  `;
  
  printWindow.document.write(html);
  printWindow.document.close();
  toast.success('PDF export opened in new window');
};

export function AnalyticsCardPopup({
  isOpen,
  onClose,
  title,
  description,
  data = [],
  columns = [],
  loading = false,
  // Chart component to render (optional)
  chartComponent,
  // Pagination
  totalItems = 0,
  itemsPerPage = 10,
  currentPage = 1,
  onPageChange,
  // Duration filter
  duration,
  onDurationChange,
  durationOptions = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '1y', label: 'Last Year' },
    { value: 'all', label: 'All Time' }
  ],
  // Additional filters (optional)
  filters,
  onFilterChange,
  // Search
  searchable = true,
  searchPlaceholder = 'Search across all columns...',
  // Export filename
  exportFilename = 'analytics_export'
}) {
  const [localPage, setLocalPage] = useState(currentPage);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  // Handle column sorting
  const handleSort = (columnKey) => {
    setSortConfig(prev => {
      if (prev.key === columnKey) {
        // Toggle direction or clear sort
        if (prev.direction === 'asc') return { key: columnKey, direction: 'desc' };
        if (prev.direction === 'desc') return { key: null, direction: 'asc' };
      }
      return { key: columnKey, direction: 'asc' };
    });
    setLocalPage(1); // Reset to first page on sort
  };
  
  // Get sort icon for column header
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    if (sortConfig.direction === 'asc') return <ArrowUp className="h-3 w-3 ml-1" />;
    return <ArrowDown className="h-3 w-3 ml-1" />;
  };
  
  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!searchQuery.trim() || !data.length) return data;
    
    const query = searchQuery.toLowerCase().trim();
    return data.filter(row => {
      // Search in all columns
      return columns.some(col => {
        const value = row[col.key];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(query);
      }) || 
      // Also search in all object values as fallback
      Object.values(row).some(val => 
        val !== null && val !== undefined && String(val).toLowerCase().includes(query)
      );
    });
  }, [data, searchQuery, columns]);
  
  // Sort filtered data
  const sortedData = useMemo(() => {
    if (!sortConfig.key || !filteredData.length) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      
      // Smart sorting based on value type
      // Check if values are dates
      if (sortConfig.key.includes('date') || sortConfig.key.includes('created') || sortConfig.key.includes('updated')) {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      // Check if values are numbers
      else if (typeof aVal === 'number' || typeof bVal === 'number' || !isNaN(Number(aVal))) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }
      // String comparison (case-insensitive)
      else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);
  
  // Calculate total based on filtered data if searching
  const displayTotal = searchQuery ? filteredData.length : totalItems;
  const displayPages = Math.ceil(displayTotal / itemsPerPage);
  
  // Get paginated data for display
  const displayData = useMemo(() => {
    const dataToUse = sortConfig.key ? sortedData : (searchQuery ? filteredData : data);
    if (searchQuery || sortConfig.key) {
      // When searching or sorting, paginate locally
      const start = (localPage - 1) * itemsPerPage;
      return dataToUse.slice(start, start + itemsPerPage);
    }
    return data;
  }, [sortedData, filteredData, data, searchQuery, sortConfig, localPage, itemsPerPage]);
  
  useEffect(() => {
    setLocalPage(currentPage);
  }, [currentPage]);
  
  // Reset to page 1 when search changes
  useEffect(() => {
    setLocalPage(1);
  }, [searchQuery]);
  
  // Reset sort and search when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSortConfig({ key: null, direction: 'asc' });
      setLocalPage(1);
    }
  }, [isOpen]);
  
  const handlePageChange = (newPage) => {
    setLocalPage(newPage);
    if (!searchQuery && onPageChange) onPageChange(newPage);
  };
  
  const handleExportCSV = () => {
    exportToCSV(searchQuery ? filteredData : data, exportFilename, columns);
  };
  
  const handleExportPDF = () => {
    exportToPDF(title, searchQuery ? filteredData : data, columns);
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-xl flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                {title}
              </DialogTitle>
              {description && (
                <DialogDescription className="mt-1">
                  {description}
                </DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>
        
        {/* Search Bar */}
        {searchable && (
          <div className="flex-shrink-0 py-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
                data-testid="analytics-popup-search"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            {searchQuery && (
              <p className="text-xs text-muted-foreground mt-1">
                Found {filteredData.length} of {data.length} records matching &quot;{searchQuery}&quot;
              </p>
            )}
          </div>
        )}
        
        {/* Toolbar: Duration Filter & Export */}
        <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-4 py-4 border-b">
          <div className="flex items-center gap-4">
            {/* Duration Filter */}
            {onDurationChange && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Select value={duration} onValueChange={onDurationChange}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    {durationOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Additional custom filters */}
            {filters}
          </div>
          
          {/* Export Buttons */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={loading || displayData.length === 0}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={loading || displayData.length === 0}>
              <FileText className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>
        
        {/* Content Area */}
        <div className="flex-1 overflow-auto py-4">
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-[200px] w-full" />
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Chart Section (if provided) */}
              {chartComponent && (
                <div className="mb-6">
                  <Card>
                    <CardContent className="pt-6">
                      {chartComponent}
                    </CardContent>
                  </Card>
                </div>
              )}
              
              {/* Data Table */}
              {columns.length > 0 && displayData.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[500px] overflow-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-muted/95 backdrop-blur-sm z-10">
                        <TableRow>
                          {columns.map((col, idx) => (
                            <TableHead 
                              key={idx} 
                              className={`${col.className || ''} cursor-pointer hover:bg-muted/80 transition-colors select-none`}
                              onClick={() => handleSort(col.key)}
                              data-testid={`sort-${col.key}`}
                            >
                              <div className="flex items-center">
                                {col.label}
                                {getSortIcon(col.key)}
                              </div>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayData.map((row, rowIdx) => (
                          <TableRow key={rowIdx} className="hover:bg-muted/30">
                            {columns.map((col, colIdx) => (
                              <TableCell key={colIdx} className={col.cellClassName}>
                                {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '-')}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              
              {displayData.length === 0 && !chartComponent && (
                <div className="text-center py-12 text-muted-foreground">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{searchQuery ? `No results matching "${searchQuery}"` : 'No data available for the selected period'}</p>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Pagination */}
        {displayPages > 1 && (
          <div className="flex-shrink-0 flex items-center justify-between pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {((localPage - 1) * itemsPerPage) + 1} to {Math.min(localPage * itemsPerPage, displayTotal)} of {displayTotal} records
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={localPage <= 1}
                onClick={() => handlePageChange(localPage - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(displayPages, 5) }, (_, i) => {
                  let pageNum;
                  if (displayPages <= 5) {
                    pageNum = i + 1;
                  } else if (localPage <= 3) {
                    pageNum = i + 1;
                  } else if (localPage >= displayPages - 2) {
                    pageNum = displayPages - 4 + i;
                  } else {
                    pageNum = localPage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={localPage === pageNum ? 'default' : 'outline'}
                      size="sm"
                      className="w-9"
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={localPage >= displayPages}
                onClick={() => handlePageChange(localPage + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Clickable Card wrapper for analytics cards
export function ClickableAnalyticsCard({ 
  children, 
  onClick, 
  className = '',
  disabled = false
}) {
  return (
    <div 
      className={`cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-lg ${disabled ? 'pointer-events-none opacity-70' : ''} ${className}`}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

export default AnalyticsCardPopup;
